import { acceptHMRUpdate, defineStore } from 'pinia'
import { ref, computed, type Ref } from 'vue'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import { type CRUDStore, type PageMeta } from '@cplug/frontend-core/stores'
import { Result } from '@cplug/frontend-core/utils'
import { ErrorCodes, MetaErrorCodes, type BaseError } from '@cplug/frontend-core/models'
import { AppKind, type StoreApp } from '~/domain/dashboard/app/apps/models/App'
import { StoreCatalogLayout } from '~/domain/dashboard/app/apps/models/AppSetting'
import { CPAppGateway } from '~/domain/dashboard/app/apps/gateways/CPAppGateway'
import CPAppCatalogProductCategoryGateway from '~/domain/dashboard/app/availability/gateways/CPAppCatalogProductCategoryGateway'
import { fetchStoreAppByCompany } from '~/domain/dashboard/store/utils/fetchStoreAppByCompany'
import { fetchTenantStoreDomain, type TenantStoreDomain } from '~/domain/dashboard/store/utils/fetchTenantStoreDomain'
import useAppStore from '~/domain/dashboard/app/apps/stores/useAppStore'
import useLegalTermStore from '~/domain/dashboard/app/apps/stores/useLegalTermStore'
import { CPOpeningHoursGateway } from '~/domain/dashboard/settings/self-service/gateways/CPOpeningHoursGateway'
import { useMoneyDefault } from '~/shared/money/composables/useMoneyDefault'
import { FulfillmentType } from '~/shared/models/Fullfilment'
import {
  mapOpeningHoursToWeeklyPeriods,
  mapWeeklyPeriodsToOpeningHours
} from '~/domain/dashboard/settings/self-service/gateways/DTOs/OpeningHourDTO'
import type { WeeklyPeriods } from '~/shared/weekly-periods/models/WeeklyPeriods'
import { useEmptyWeeklyPeriods } from '~/shared/weekly-periods/composables/useEmptyWeeklyPeriods'
import { useObjectClone } from '~/shared/composables/useObjectClone'
import { toUrlNoCache } from '~/shared/composables/useImage'
import { useErrorFormaterByMeta } from '~/shared/composables/useErrorFormatter'
import { useBrandTheme } from '~/domain/shared/branding/composables/useBrandTheme'
import { useTimezoneFromCompany } from '~/domain/dashboard/store/composables/useTimezoneFromCompany'
import {
  STORE_DOMAIN_MAX_LENGTH,
  STORE_DOMAIN_REGEX
} from '~/domain/dashboard/store/constants/storeBasicInfoLimits'
import useStoreSyncStore from '~/domain/dashboard/store/store-sync/stores/useStoreSyncStore'
import {
  createDefaultLegalTerms,
  createEmptyLegalTerm,
  ensureStoreSettingsLegalTerms,
  mapLegalTermsFromList,
  STORE_EDITABLE_LEGAL_TERM_TYPES,
  type StoreLegalTermsModel
} from '~/domain/dashboard/store/models/StoreLegalTermsModel'
import {
  isValidLegalTermExternalLink,
  isLegalTermTextEmpty,
  resolveLegalTermContentType,
  prepareLegalTermForSave
} from '~/domain/dashboard/app/apps/composables/useLegalTermHelpers'

export interface StoreSettingsModel {
  app: StoreApp
  deliveryPeriods: WeeklyPeriods
  pickupPeriods: WeeklyPeriods
  legalTerms: StoreLegalTermsModel
  pendingLogoFile?: File
}

type StoreSettingsStore = CRUDStore<StoreSettingsModel> & {
  createDefaultModel: (existingApp?: StoreApp) => StoreSettingsModel
  isLoaded: Ref<boolean>
  resolvedStoreAppId: Ref<number | null>
  openingHoursLoaded: Ref<boolean>
  openingHoursLoading: Ref<boolean>
  shouldSaveHours: Ref<boolean>
  shouldLoadHours: Ref<boolean>
  shouldSaveCategories: Ref<boolean>
  shouldSaveLegalTerms: Ref<boolean>
  getIfNeeded: () => Promise<void>
  resolveStoreAppId: () => Promise<number | null>
  loadOpeningHoursIfNeeded: () => Promise<void>
  invalidate: () => void
}

const SOURCE_TYPE = 'store'
const FULFILLMENT_DELIVERY = 'delivery'
const FULFILLMENT_PICKUP = 'pickup'

function weeklyPeriodsHasHours (periods: WeeklyPeriods): boolean {
  return Object.values(periods).some(day => day.length > 0)
}

function modelHasConfiguredHours (model: StoreSettingsModel): boolean {
  return weeklyPeriodsHasHours(model.deliveryPeriods) || weeklyPeriodsHasHours(model.pickupPeriods)
}

const useStoreSettingsStore = defineStore('store/settings', (): StoreSettingsStore => {
  const appStore = useAppStore()
  const legalTermStore = useLegalTermStore()
  const { primaryColor: brandPrimaryColor } = useBrandTheme()
  const openingHoursGateway = CPOpeningHoursGateway.instance()
  const inferredTimezone = useTimezoneFromCompany()

  const model = ref<StoreSettingsModel>()
  const pageList = ref<StoreSettingsModel[]>()
  const pageMeta = ref<PageMeta>({
    loading: false,
    page: 1,
    perPage: 20,
    total: 0,
    totalPage: 0,
    lastPage: 0,
    filters: {}
  })
  const saveErrors = ref<BaseError[]>([])
  const isLoaded = ref(false)
  const loadedCompanyId = ref<number | null>(null)
  const resolvedStoreAppId = ref<number | null>(null)
  const storeAppResolved = ref(false)

  const currentCompanyId = (): number | null =>
    useAuthenticationStore().companies[0]?.id ?? null

  const isStaleForCurrentTenant = (): boolean => {
    const companyId = currentCompanyId()
    if (companyId == null) {
      return loadedCompanyId.value != null
    }
    return loadedCompanyId.value !== companyId
  }
  let resolveStoreAppIdPromise: Promise<number | null> | null = null
  const openingHoursLoaded = ref(false)
  const openingHoursLoading = ref(false)
  let openingHoursLoadPromise: Promise<void> | null = null
  const shouldSaveHours = ref(true)
  const shouldLoadHours = ref(true)
  const shouldSaveCategories = ref(false)
  const shouldSaveLegalTerms = ref(false)
  const loadedAppDomain = ref<string | null>(null)

  const errors = computed(() => [...appStore.errors, ...saveErrors.value])

  const createDefaultModel = (existingApp?: StoreApp): StoreSettingsModel => {
    const base = existingApp ?? {} as Partial<StoreApp>

    const app: StoreApp = {
      ...base,
      name: base.name ?? '',
      kind: AppKind.Store,
      settings: {
        basicInfo: base.settings?.basicInfo ?? { name: '', timezone: inferredTimezone.value },
        theme: {
          primaryColor: brandPrimaryColor.value ?? '#000000',
          secondaryColor: brandPrimaryColor.value ?? '#000000',
          ...(base.settings?.theme ?? {}),
          catalogLayout: base.settings?.theme?.catalogLayout ?? StoreCatalogLayout.List
        },
        contact: base.settings?.contact ?? { channels: [] },
        socialMedia: base.settings?.socialMedia ?? [],
        fulfillment: {
          ...(base.settings?.fulfillment ?? {
            minPickupMinutes: 0,
            minPickupOrderValue: useMoneyDefault(),
            minDeliveryOrderValue: useMoneyDefault(),
            freeDeliveryFeeFromOrder: useMoneyDefault()
          }),
          fulfillmentsAvailable: (base.settings?.fulfillment?.fulfillmentsAvailable?.length ?? 0) > 0
            ? base.settings!.fulfillment!.fulfillmentsAvailable
            : [FulfillmentType.Pickup]
        }
      },
      categories: base.categories ?? []
    } as StoreApp

    return {
      app,
      deliveryPeriods: useEmptyWeeklyPeriods(),
      pickupPeriods: useEmptyWeeklyPeriods(),
      legalTerms: createDefaultLegalTerms(base.id)
    }
  }

  const applyTenantStoreDomain = (app: StoreApp, tenantDomain: TenantStoreDomain): void => {
    app.domain = tenantDomain.domain
    app.domainEditable = tenantDomain.domainEditable
  }

  const bootstrapDefaultModel = async (): Promise<StoreSettingsModel> => {
    const defaultModel = createDefaultModel()
    const tenantDomain = await fetchTenantStoreDomain()

    if (tenantDomain.isOk()) {
      applyTenantStoreDomain(defaultModel.app, tenantDomain.getValue())
      loadedAppDomain.value = tenantDomain.getValue().domain
    } else {
      saveErrors.value = [...saveErrors.value, tenantDomain.getError()]
    }

    return defaultModel
  }

  const fetchLegalTerms = async (appId: number): Promise<StoreLegalTermsModel> => {
    await legalTermStore.fetchByApp(appId)

    return mapLegalTermsFromList(legalTermStore.legalTermsByApp, appId)
  }

  const fetchStoreAppForCurrentCompany = (): Promise<Result<StoreApp | null>> => {
    const companyId = currentCompanyId()
    if (companyId == null) {
      return Promise.resolve(Result.ofError({ code: ErrorCodes.Unknown }))
    }

    return fetchStoreAppByCompany(companyId)
  }

  const resolveStoreAppId = (): Promise<number | null> => {
    const modelAppId = model.value?.app?.id
    if (modelAppId != null) {
      resolvedStoreAppId.value = modelAppId
      storeAppResolved.value = true
      return Promise.resolve(modelAppId)
    }

    if (storeAppResolved.value) {
      return Promise.resolve(resolvedStoreAppId.value)
    }

    if (resolvedStoreAppId.value != null) {
      return Promise.resolve(resolvedStoreAppId.value)
    }

    if (resolveStoreAppIdPromise) {
      return resolveStoreAppIdPromise
    }

    resolveStoreAppIdPromise = (async (): Promise<number | null> => {
      try {
        const result = await fetchStoreAppForCurrentCompany()
        if (!result.isOk()) {
          resolvedStoreAppId.value = null
          return null
        }

        const appId = result.getValue()?.id ?? null
        resolvedStoreAppId.value = appId
        storeAppResolved.value = true
        return appId
      } catch {
        resolvedStoreAppId.value = null
        return null
      } finally {
        resolveStoreAppIdPromise = null
      }
    })()

    return resolveStoreAppIdPromise
  }

  const validateLegalTerms = (modelInstance: StoreSettingsModel): boolean => {
    const modelWithLegalTerms = ensureStoreSettingsLegalTerms(modelInstance)
    saveErrors.value = saveErrors.value.filter(error =>
      !Object.keys(error.meta ?? {}).some(key => key.startsWith('legalTerms.'))
    )

    const validationErrors: BaseError[] = []

    for (const type of STORE_EDITABLE_LEGAL_TERM_TYPES) {
      const term = modelWithLegalTerms.legalTerms[type]
      const contentType = resolveLegalTermContentType(term)

      if (contentType === 'external_link') {
        const link = term.externalLink?.trim() ?? ''
        if (!link) {
          validationErrors.push({
            code: ErrorCodes.DataValidation,
            meta: { [`legalTerms.${type}.externalLink`]: MetaErrorCodes.Empty }
          })
        } else if (!isValidLegalTermExternalLink(link)) {
          validationErrors.push({
            code: ErrorCodes.DataValidation,
            meta: { [`legalTerms.${type}.externalLink`]: MetaErrorCodes.WrongFormat }
          })
        }
        continue
      }

      if (isLegalTermTextEmpty(term.text)) {
        validationErrors.push({
          code: ErrorCodes.DataValidation,
          meta: { [`legalTerms.${type}.text`]: MetaErrorCodes.Empty }
        })
      }
    }

    if (validationErrors.length > 0) {
      saveErrors.value = [...saveErrors.value, ...validationErrors]
      return false
    }

    return true
  }

  const persistLegalTerms = async (modelInstance: StoreSettingsModel): Promise<boolean> => {
    const modelWithLegalTerms = ensureStoreSettingsLegalTerms(modelInstance)
    const appId = modelWithLegalTerms.app.id
    if (appId == null) { return true }

    for (const type of STORE_EDITABLE_LEGAL_TERM_TYPES) {
      const term = modelWithLegalTerms.legalTerms[type]
      const contentType = resolveLegalTermContentType(term)
      const termToSave = prepareLegalTermForSave({
        ...term,
        title: term.title?.trim() || createEmptyLegalTerm(type, appId).title,
        appId
      }, contentType)

      if (!await legalTermStore.save(termToSave)) {
        saveErrors.value = [...saveErrors.value, ...legalTermStore.errors]
        return false
      }
    }

    modelInstance.legalTerms = await fetchLegalTerms(appId)
    return true
  }

  const fetchOpeningHoursPeriods = async (): Promise<{
    deliveryPeriods: WeeklyPeriods
    pickupPeriods: WeeklyPeriods
  }> => {
    const deliveryResult = await openingHoursGateway.list(1, 100, {
      source_type: SOURCE_TYPE,
      fulfillment_type: FULFILLMENT_DELIVERY
    })
    const pickupResult = await openingHoursGateway.list(1, 100, {
      source_type: SOURCE_TYPE,
      fulfillment_type: FULFILLMENT_PICKUP
    })

    return {
      deliveryPeriods: mapOpeningHoursToWeeklyPeriods(
        deliveryResult.isOk() ? deliveryResult.getValue().contentList : []
      ),
      pickupPeriods: mapOpeningHoursToWeeklyPeriods(
        pickupResult.isOk() ? pickupResult.getValue().contentList : []
      )
    }
  }

  const loadOpeningHoursIfNeeded = (): Promise<void> => {
    if (!shouldLoadHours.value || model.value == null) {
      return Promise.resolve()
    }

    if (openingHoursLoaded.value) {
      return Promise.resolve()
    }

    if (openingHoursLoadPromise) {
      return openingHoursLoadPromise
    }

    openingHoursLoadPromise = (async (): Promise<void> => {
      openingHoursLoading.value = true

      try {
        const fetched = await fetchOpeningHoursPeriods()
        const fetchedHasHours = weeklyPeriodsHasHours(fetched.deliveryPeriods) ||
          weeklyPeriodsHasHours(fetched.pickupPeriods)
        const currentModel = model.value!

        // Avoid wiping hours saved during onboarding when the API has not propagated yet.
        if (fetchedHasHours || !modelHasConfiguredHours(currentModel)) {
          model.value = {
            ...currentModel,
            deliveryPeriods: fetched.deliveryPeriods,
            pickupPeriods: fetched.pickupPeriods
          }
        }

        openingHoursLoaded.value = true
      } catch {
        // Keep cached periods on failure so the panel can still render.
      } finally {
        openingHoursLoading.value = false
        openingHoursLoadPromise = null
      }
    })()

    return openingHoursLoadPromise
  }

  const get = async (): Promise<void> => {
    pageMeta.value.loading = true
    saveErrors.value = []

    try {
      const result = await fetchStoreAppForCurrentCompany()

      if (!result.isOk()) {
        saveErrors.value = [result.getError()]
        resolvedStoreAppId.value = null
        return
      }

      storeAppResolved.value = true
      const storeApp = result.getValue()

      if (storeApp == null) {
        model.value = await bootstrapDefaultModel()
        resolvedStoreAppId.value = null
        return
      }

      if (storeApp.id != null) {
        resolvedStoreAppId.value = storeApp.id
      }

      const openingHours = shouldLoadHours.value
        ? await fetchOpeningHoursPeriods()
        : {
            deliveryPeriods: useEmptyWeeklyPeriods(),
            pickupPeriods: useEmptyWeeklyPeriods()
          }

      const appClone = useObjectClone(storeApp)
      if (!appClone.settings.basicInfo.timezone) {
        appClone.settings.basicInfo.timezone = inferredTimezone.value
      }
      if (!appClone.settings.theme.catalogLayout) {
        appClone.settings.theme.catalogLayout = StoreCatalogLayout.List
      }

      loadedAppDomain.value = appClone.domain ?? null

      const legalTerms = storeApp.id != null
        ? await fetchLegalTerms(storeApp.id)
        : createDefaultLegalTerms(storeApp.id)

      model.value = {
        app: appClone,
        deliveryPeriods: openingHours.deliveryPeriods,
        pickupPeriods: openingHours.pickupPeriods,
        legalTerms
      }

      if (shouldLoadHours.value) {
        openingHoursLoaded.value = true
      }
    } catch {
      saveErrors.value = [{ code: ErrorCodes.Unknown }]
    } finally {
      pageMeta.value.loading = false
      isLoaded.value = true
      loadedCompanyId.value = currentCompanyId()
    }
  }

  const getIfNeeded = async (): Promise<void> => {
    if (isStaleForCurrentTenant()) {
      invalidate()
    }

    if (!isLoaded.value || !model.value) {
      await get()
    } else if (model.value.app?.id == null) {
      storeAppResolved.value = false
      const resolvedId = await resolveStoreAppId()
      if (resolvedId != null) {
        await get()
      }
    }

    await loadOpeningHoursIfNeeded()
  }

  const invalidate = (): void => {
    isLoaded.value = false
    model.value = undefined
    pageList.value = undefined
    loadedCompanyId.value = null
    resolvedStoreAppId.value = null
    storeAppResolved.value = false
    resolveStoreAppIdPromise = null
    openingHoursLoaded.value = false
    openingHoursLoading.value = false
    openingHoursLoadPromise = null
  }

  const persistPendingLogo = async (modelInstance: StoreSettingsModel): Promise<boolean> => {
    if (!modelInstance.pendingLogoFile) {
      return true
    }

    const appId = modelInstance.app.id
    if (appId == null) {
      return true
    }

    const logoResult = await uploadLogoFile(appId, modelInstance.pendingLogoFile)
    if (!logoResult) {
      return false
    }

    modelInstance.app.imageUrl = toUrlNoCache(logoResult) ?? logoResult
    modelInstance.pendingLogoFile = undefined
    return true
  }

  const normalizeDomainBeforeSave = (modelInstance: StoreSettingsModel): void => {
    const app = modelInstance.app

    if (!app.domainEditable) {
      app.domain = loadedAppDomain.value
      return
    }

    const next = (app.domain ?? '').trim().toLowerCase()

    if (next === '') {
      app.domain = loadedAppDomain.value
      return
    }

    app.domain = next
  }

  const validateDomain = (modelInstance: StoreSettingsModel): boolean => {
    const app = modelInstance.app

    if (!app.domainEditable) {
      return true
    }

    const next = (app.domain ?? '').trim().toLowerCase()
    const loaded = (loadedAppDomain.value ?? '').trim().toLowerCase()

    if (next === '' || next === loaded) {
      return true
    }

    if (next.length > STORE_DOMAIN_MAX_LENGTH || !STORE_DOMAIN_REGEX.test(next)) {
      saveErrors.value = [...saveErrors.value, {
        code: ErrorCodes.DataValidation,
        meta: { domain: MetaErrorCodes.WrongFormat }
      }]
      return false
    }

    return true
  }

  const save = async (modelInstance: StoreSettingsModel): Promise<boolean> => {
    saveErrors.value = []

    normalizeDomainBeforeSave(modelInstance)

    if (!validate(modelInstance)) {
      return false
    }

    if (!await persistPendingLogo(modelInstance)) {
      return false
    }

    if (!await appStore.save(modelInstance.app)) {
      return false
    }

    const domainChanged = modelInstance.app.domainEditable &&
      (modelInstance.app.domain ?? '').trim().toLowerCase() !== (loadedAppDomain.value ?? '').trim().toLowerCase()

    if (domainChanged) {
      useStoreSyncStore().markPartStale('merchant')
      loadedAppDomain.value = modelInstance.app.domain ?? null
    }

    // Sync the id back to modelInstance after create (BaseCRUDStore.save only updates appStore.model.value)
    if (appStore.model?.id != null) {
      modelInstance.app.id = appStore.model.id
    }

    if (!await persistPendingLogo(modelInstance)) {
      return false
    }

    if (shouldSaveCategories.value && !await persistCategoriesPreparationTime(modelInstance)) {
      return false
    }

    let saveSuccess = true

    if (shouldSaveLegalTerms.value) {
      saveSuccess = await persistLegalTerms(modelInstance)
    }

    let hoursSuccess = true

    if (saveSuccess && shouldSaveHours.value) {
      // const deliveryHours = mapWeeklyPeriodsToOpeningHours(modelInstance.deliveryPeriods)
      // const deliveryResult = await openingHoursGateway.save(deliveryHours, SOURCE_TYPE, FULFILLMENT_DELIVERY)

      // deliveryResult.ifError((error) => {
      //   saveErrors.value = [...saveErrors.value, ...useErrorFormaterByMeta(error)]
      //   if (saveErrors.value.length === 0) {
      //     saveErrors.value = [error]
      //   }
      // })

      const pickupHours = mapWeeklyPeriodsToOpeningHours(modelInstance.pickupPeriods)
      const pickupResult = await openingHoursGateway.save(pickupHours, SOURCE_TYPE, FULFILLMENT_PICKUP)

      pickupResult.ifError((error) => {
        saveErrors.value = [...saveErrors.value, ...useErrorFormaterByMeta(error)]
        if (saveErrors.value.length === 0) {
          saveErrors.value = [error]
        }
      })

      // hoursSuccess = deliveryResult.isOk() && pickupResult.isOk()
      hoursSuccess = pickupResult.isOk()
    }

    if (hoursSuccess && saveSuccess) {
      model.value = useObjectClone(ensureStoreSettingsLegalTerms(modelInstance))
      if (shouldSaveHours.value) {
        openingHoursLoaded.value = true
      }
      // const appId = modelInstance.app.id
      // if (appId) {
      //   useStoreSyncStore().fetchStatus(appId)
      // }
    }

    return hoursSuccess && saveSuccess
  }

  /**
   * Persiste o `preparation_time` de cada categoria via `PUT /apps/{appId}/categories`.
   * Categorias "removidas" pela UI vêm com `preparationTimeMinutes = 0` (a UI as esconde,
   * mas mantém no array justamente para que o backend zere o tempo adicional).
   * O endpoint faz upsert sem alterar `position`/`next_category_id`.
   */
  const persistCategoriesPreparationTime = async (modelInstance: StoreSettingsModel): Promise<boolean> => {
    const appId = modelInstance.app.id
    const categories = modelInstance.app.categories ?? []
    if (appId == null || categories.length === 0) {
      return true
    }

    const payload = categories
      .filter(c => c.catalogCategoryId != null)
      .map(c => ({
        id: c.catalogCategoryId as number,
        preparationTimeMinutes: c.preparationTimeMinutes ?? 0
      }))

    const result = await CPAppCatalogProductCategoryGateway.instance()
      .updateCatalogProductCategories(appId, payload)

    result.ifError((error) => {
      const formatted = useErrorFormaterByMeta(error)
      saveErrors.value = [...saveErrors.value, ...(formatted.length > 0 ? formatted : [error])]
    })

    return result.isOk()
  }

  const uploadLogoFile = (appId: number, file: File): Promise<string | undefined> => {
    return new Promise((resolve) => {
      const reader = new FileReader()
      reader.onload = async (e: ProgressEvent<FileReader>): Promise<void> => {
        const base64 = e.target?.result as string | undefined
        if (!base64) {
          resolve(undefined)
          return
        }

        const appGateway = CPAppGateway.instance()
        const result = await appGateway.uploadImage(appId, base64)

        result
          .ifSuccess(imageUrl => resolve(imageUrl))
          .ifError((error) => {
            saveErrors.value = [...saveErrors.value, ...useErrorFormaterByMeta(error)]
            resolve(undefined)
          })
      }
      reader.onerror = (): void => resolve(undefined)
      reader.readAsDataURL(file)
    })
  }

  const validate = (modelInstance: StoreSettingsModel): boolean => {
    if (!appStore.validate(modelInstance.app)) {
      return false
    }

    if (!validateDomain(modelInstance)) {
      return false
    }

    if (shouldSaveLegalTerms.value) {
      return validateLegalTerms(modelInstance)
    }

    return true
  }

  const getPage = (): Promise<void> => {
    pageList.value = model.value ? [model.value] : []
    return Promise.resolve()
  }

  const filter = (): Promise<void> => {
    return get()
  }

  const remove = (): Promise<boolean> => {
    return Promise.resolve(false)
  }

  const updateLocal = (): void => {}

  const reload = async (): Promise<void> => {
    await get()
  }

  const getAll = async (): Promise<void> => {
    await get()
  }

  return {
    model,
    pageList,
    pageMeta,
    errors,
    isLoaded,
    resolvedStoreAppId,
    openingHoursLoaded,
    openingHoursLoading,
    shouldSaveHours,
    shouldLoadHours,
    shouldSaveCategories,
    shouldSaveLegalTerms,
    get,
    getIfNeeded,
    resolveStoreAppId,
    loadOpeningHoursIfNeeded,
    invalidate,
    getPage,
    filter,
    remove,
    save,
    validate,
    updateLocal,
    reload,
    getAll,
    createDefaultModel,
    filterAndAppend: filter
  }
})

export default useStoreSettingsStore

/* c8 ignore next 3 */
if ((import.meta as any).hot) {
  (import.meta as any).hot.accept(acceptHMRUpdate(useStoreSettingsStore, (import.meta as any).hot))
}
